import { EyeIcon, EyeOffIcon } from 'lucide-react'
import React, { useState } from 'react'

import { renderFormattedSSN } from 'components/members/utils'
import { IconButton } from 'slab'
import { capitalize, cn, getFormattedDate } from 'utils'
import type {
  MembersCoveredParticipant,
  MembersEnrollmentOverviewProgressState,
} from 'utils/web_api.schemas'

import EnrollmentProgress from '../EnrollmentProgress/EnrollmentProgress'

interface CoveredDependentProps {
  coveredParticipant: MembersCoveredParticipant;
  isPriorYear?: boolean;
  planIsInactive?: boolean;
  participantIsInactive?: boolean;
  memberId?: string;
  participantEnrollmentProgressState?: MembersEnrollmentOverviewProgressState;
  children?: React.ReactNode;
}

const LABEL_CONTAINER_CLASSES =
  'flex items-center gap-1 body text-gray-500 mb-1 h-5'

const CoveredDependent: React.FC<CoveredDependentProps> = ({
  coveredParticipant,
  isPriorYear = false,
  planIsInactive = false,
  participantIsInactive = false,
  memberId,
  participantEnrollmentProgressState,
  children,
}) => {
  const [showFullSSN, setShowFullSSN] = useState(false)

  // TODO: Convert qualifying_life_event_status to MembersEnrollmentProgressState

  const toggleSSNVisibility = () => {
    setShowFullSSN(!showFullSSN)
  }

  return (
    <div
      className={cn(
        'border border-gray-200 rounded-lg p-3 shadow-sm',
        isPriorYear ||
          planIsInactive ||
          participantIsInactive ||
          participantEnrollmentProgressState
          ? 'bg-thatch-50'
          : 'bg-white'
      )}
    >
      <div className='flex justify-between items-center gap-2 mb-3'>
        <div className='flex items-center gap-2'>
          <h3 className='body-bold'>
            {coveredParticipant.given_name} {coveredParticipant.family_name}
          </h3>
          <span className='body text-gray-500'>
            {capitalize(coveredParticipant.relationship)}
          </span>
        </div>
        {participantEnrollmentProgressState && (
          <EnrollmentProgress
            state={participantEnrollmentProgressState}
            showCaption={false}
          />
        )}
      </div>
      <div className='grid grid-cols-3 gap-4'>
        <div>
          <div className={LABEL_CONTAINER_CLASSES}>
            <span>Member ID</span>
          </div>
          <div className='body'>
            {memberId ||
              (participantEnrollmentProgressState && (
                <span className='text-gray-500'>Pending</span>
              )) ||
              (!participantEnrollmentProgressState && '—')}
          </div>
        </div>

        <div>
          <div className={LABEL_CONTAINER_CLASSES}>
            <span>Date of birth</span>
          </div>
          <div className='body'>
            {getFormattedDate(coveredParticipant.dob + 'T00:00:00')}
          </div>
        </div>

        <div>
          <div className={LABEL_CONTAINER_CLASSES}>
            <span>SSN</span>
            {coveredParticipant.ssn && (
              <IconButton
                icon={showFullSSN ? EyeOffIcon : EyeIcon}
                label={showFullSSN ? 'Hide SSN' : 'Show SSN'}
                onClick={toggleSSNVisibility}
              />
            )}
          </div>
          {coveredParticipant.ssn
            ? (
              <div className='body font-mono'>
                {renderFormattedSSN(coveredParticipant.ssn, showFullSSN)}
              </div>
              )
            : (
              <div className='body'>—</div>
              )}
        </div>
      </div>

      {children && (
        <div>
          <hr className='border-gray-200 -mx-3 my-3' />
          <span className='body-sm text-gray-500'>{children}</span>
        </div>
      )}
    </div>
  )
}

export default CoveredDependent
